import { useMemo, useState } from "react";
import { ArrowLeft, Check, Star, X } from "lucide-react";
import { SpeakButton } from "./SpeakButton";
import { words as wordsApi } from "../lib/api";

const shuffle = (a) => [...a].sort(() => Math.random() - 0.5);
const MIN_WORDS = 3;

const buildRounds = (list) =>
  shuffle(list).slice(0, 10).map((w) => ({
    word: w.word,
    answer: w.meaning,
    options: shuffle([w.meaning, ...shuffle(list.filter((o) => o.word !== w.word)).slice(0, 3).map((o) => o.meaning)]),
  }));

const Finished = ({ score, total, onAgain, onBack }) => {
  const stars = score === total ? 3 : score >= total / 2 ? 2 : score > 0 ? 1 : 0;
  return (
    <div data-testid="quiz-finished" className="text-center py-6">
      <div className="flex justify-center gap-2 text-[#FBBF24]">
        {[0, 1, 2].map((i) => <Star key={i} size={34} fill={i < stars ? "currentColor" : "none"} className="what-quiz-star" style={{ animationDelay: `${i * 0.15}s` }} />)}
      </div>
      <p data-testid="quiz-score" className="font-display text-3xl font-bold text-white mt-4">{score} out of {total}</p>
      <p className="text-slate-400 mt-1">{stars === 3 ? "Wow, you know all your words!" : stars >= 2 ? "Great job! Keep going!" : "Nice try! Play again to find out more."}</p>
      <div className="flex justify-center gap-3 mt-6">
        <button data-testid="quiz-play-again-btn" onClick={onAgain} className="what-save-word">Play again</button>
        <button data-testid="quiz-back-btn" onClick={onBack} className="what-btn-ghost">My Words</button>
      </div>
    </div>
  );
};

export const WordQuiz = ({ list, onBack }) => {
  const [seed, setSeed] = useState(0);
  const rounds = useMemo(() => buildRounds(list), [list, seed]); // eslint-disable-line react-hooks/exhaustive-deps
  const [i, setI] = useState(0);
  const [picked, setPicked] = useState(null);
  const [score, setScore] = useState(0);

  if (list.length < MIN_WORDS) {
    return (
      <div data-testid="quiz-not-enough" className="py-6 text-center">
        <p className="text-slate-300">Save at least {MIN_WORDS} words to play the quiz. You have {list.length}.</p>
        <button data-testid="quiz-back-btn" onClick={onBack} className="what-btn-ghost mt-4 mx-auto">My Words</button>
      </div>
    );
  }
  const again = () => { setSeed((s) => s + 1); setI(0); setPicked(null); setScore(0); };
  if (i >= rounds.length) return <Finished score={score} total={rounds.length} onAgain={again} onBack={onBack} />;

  const r = rounds[i];
  const pick = (opt) => {
    if (picked) return;
    setPicked(opt);
    const nextScore = score + (opt === r.answer ? 1 : 0);
    setScore(nextScore);
    if (i === rounds.length - 1) wordsApi.quiz(nextScore, rounds.length).catch(() => {});
  };
  return (
    <div data-testid="word-quiz">
      <div className="flex items-center justify-between text-xs font-mono text-slate-500">
        <button data-testid="quiz-exit-btn" onClick={onBack} className="flex items-center gap-1 hover:text-white"><ArrowLeft size={13} /> back</button>
        <span data-testid="quiz-progress">Question {i + 1} of {rounds.length} · {score} correct</span>
      </div>
      <p className="text-slate-400 text-sm mt-5">What does this word mean?</p>
      <div className="flex items-center gap-3 mt-1">
        <p data-testid="quiz-word" className="font-display text-3xl font-extrabold text-white capitalize">{r.word}</p>
        <SpeakButton big load={() => wordsApi.listen(r.word)} testid="quiz-listen-btn" />
      </div>
      <div className="space-y-2 mt-5">
        {r.options.map((opt, k) => {
          const state = !picked ? "" : opt === r.answer ? "is-right" : opt === picked ? "is-wrong" : "is-dim";
          return (
            <button key={k} data-testid={`quiz-option-${k}`} data-correct={opt === r.answer} onClick={() => pick(opt)} className={`what-quiz-option ${state}`}>
              <span className="flex-1 text-left">{opt}</span>
              {state === "is-right" && <Check size={18} />}
              {state === "is-wrong" && <X size={18} />}
            </button>
          );
        })}
      </div>
      {picked && (
        <div className="flex items-center justify-between mt-5">
          <p data-testid="quiz-feedback" className={picked === r.answer ? "text-[#4ADE80] font-semibold" : "text-[#FCA5A5] font-semibold"}>
            {picked === r.answer ? "Yes! That's right!" : "Not quite. The green one is right!"}
          </p>
          <button data-testid="quiz-next-btn" onClick={() => { setI(i + 1); setPicked(null); }} className="what-save-word">
            {i === rounds.length - 1 ? "See my stars" : "Next"}
          </button>
        </div>
      )}
    </div>
  );
};
